import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { combineClassNames } from 'app/functions';
import { HOMEPAGE_DEFAULT_SLIDER_TAB } from 'constants/homepage-types';
import arrowIcon from 'assets/icons/slider-arrow-blue.svg';
import './index.scss';
import 'swiper/css';
import Analytics from 'app/analytics';

const getSlidesPerView = (view) => {
    if (view >= 1500) return 5;
    if (view >= 1200) return 4;
    if (view >= 768) return 3;

    return 2;
};

const getBreakpointSettings = (view) => {
    const res = {
        slidesOffsetAfter: 14,
        slidesOffsetBefore: 14,
        spaceBetween: 14,
        slidesPerView: getSlidesPerView(view),
    };

    switch (view) {
        case 768:
        case 1200:
        case 1500: {
            res.slidesOffsetAfter = 0;
            res.slidesOffsetBefore = 0;
            res.spaceBetween = 10;
            break;
        }
    }

    return res;
};

const SliderArrow = ({ className, onClick, iconPath }) => (
    <button
        type="button"
        className={combineClassNames('homepage-slider-arrow', className)}
        onClick={onClick}
    >
        <img src={iconPath} alt="Arrow Icon" className="slider-arrow-icon" />
    </button>
);

const SliderContent = ({
    itemsList = [],
    promotionSlide = null,
    selectedTab = HOMEPAGE_DEFAULT_SLIDER_TAB,
    isLoading = false,
    creativeSlot
}) => {
    const deviceState = useSelector((store) => store.state.deviceState);

    const isMobile = (deviceState === 'mobile');
    const isMobileOrTablet = (isMobile || deviceState === 'tablet');

    const [arrows, setArrows] = useState({ next: true, prev: false });

    const swiperRef = useRef(null);

    const hasScrolling = promotionSlide
        ? itemsList.length >= getSlidesPerView(window.innerWidth)
        : itemsList.length > getSlidesPerView(window.innerWidth);

    const swiperOptions = {
        className: combineClassNames(
            'homepage-slider-content',
            isLoading && 'homepage-slider-content_loading',
        ),
        ...getBreakpointSettings(window.innerWidth),
        allowTouchMove: (isMobileOrTablet || hasScrolling),
        breakpoints: {
            0: getBreakpointSettings(320),
            768: getBreakpointSettings(768),
            1200: getBreakpointSettings(1200),
            1500: getBreakpointSettings(1500),
        },
    };

    const checkArrows = () => setArrows({
        next: swiperRef.current.isEnd,
        prev: swiperRef.current.isBeginning,
    });

    const onSwiperInit = (swiper) => {
        swiperRef.current = swiper;
        checkArrows();
    };

    const handleNextClick = () => {
        creativeSlot && Analytics.logSliderCarouselScrollForward(creativeSlot)
        const currentIndex = swiperRef.current.activeIndex;
        swiperRef.current.slideTo(currentIndex + 1);
    };

    const handlePrevClick = () => {
        creativeSlot && Analytics.logSliderCarouselScrollBack(creativeSlot)
        const currentIndex = swiperRef.current.activeIndex;
        swiperRef.current.slideTo(currentIndex - 1);
    };

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.slideTo(0);
            checkArrows();
        }
    }, [itemsList, selectedTab]);

    return (
        <>
            <Swiper onSwiper={onSwiperInit} onSlideChange={checkArrows} {...swiperOptions}>
                {itemsList.map((itemElement, itemIndex) => (
                    <SwiperSlide key={`slide-${selectedTab}-${itemIndex}`}>
                        {itemElement}
                    </SwiperSlide>
                ))}

                {promotionSlide && hasScrolling && (
                    <SwiperSlide key="promotion-slide">
                        {promotionSlide}
                    </SwiperSlide>
                )}
            </Swiper>

            {!isMobile && hasScrolling && (
                <>
                    <SliderArrow
                        className={combineClassNames(arrows.next && 'gray-out')}
                        onClick={handleNextClick}
                        iconPath={arrowIcon}
                    />

                    <SliderArrow
                        className={combineClassNames('flip', arrows.prev && 'gray-out')}
                        onClick={handlePrevClick}
                        iconPath={arrowIcon}
                    />
                </>
            )}
        </>
    );
};

export default SliderContent;
